import AppSettings from 'app/appSettings';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';

export interface Calc {
  [key: string]: string | undefined;
  id: string;
  name: string;
  description: string;
  type: string;
}

export interface Supplier {
  name: string;
  label: string;
}

class FragnetServiceImpl {
  token?: string;
  private _calculations?: Promise<Calc[]>;
  private _suppliers?: Promise<Supplier[]>;

  setToken(token: string) {
    this.token = token;
  }

  private _fetchCalculations = (mock = false): Promise<any> => {
    /* Get the available calculations from the calcs endpoint */
    if (mock) {
      return Promise.resolve({
        data: [
          { id: 'MW', name: 'molw', description: 'Mol weight', type: 'number' },
          { id: 'LOGP', name: 'logp', description: 'cLogP', type: 'number' },
          { id: 'ROTB', name: 'rotb', description: 'RotBonds', type: 'integer' },
          { id: 'HBD', name: 'hbd', description: 'HBond donors', type: 'integer' },
          { id: 'HBA', name: 'hba', description: 'HBond acceptors', type: 'integer' },
          { id: 'TPSA', name: 'tpsa', description: 'TPSA', type: 'number' },
          {
            id: 'SIM_RDKIT_TANIMOTO',
            name: 'sim_rdkit_tanimoto',
            description: 'Tanimoto sim (RDKit)',
            type: 'number',
          },
        ],
      });
    }
    const options: AxiosRequestConfig = {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : undefined,
    };

    return axios.get(`${AppSettings.FRAGNET_SEARCH_SERVER}/${AppSettings.CALCS}`, options);
  };

  getCalculations = (): Promise<Calc[]> => {
    if (!this._calculations) {
      this._calculations = this._fetchCalculations().then((res) => res.data);
    }
    return this._calculations;
  };

  getAvailability = (smiles: string, mock = false): Promise<any> => {
    if (mock) {
      return Promise.resolve({
        data: {
          smiles: 'CC(C)OCc1ccc(CNC(=O)CCCCNC(N)=O)cc1',
          items: [
            {
              supplier: 'MolPort',
              code: 'MOLPORT:020-059-849',
              smiles: 'CC(C)OCC1=CC=C(CNC(=O)CCCCNC(N)=O)C=C1',
            },
          ],
        },
      });
    }

    const options: AxiosRequestConfig = {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : undefined,
    };

    return axios.get(
      `${AppSettings.FRAGNET_SEARCH_SERVER}/${AppSettings.AVAILABILITY}/${encodeURIComponent(
        smiles,
      )}`,
      options,
    );
  };

  private _fetchSuppliers = (mock = true): Promise<any> => {
    if (mock) {
      return Promise.resolve({
        data: [
          { name: 'MolPort', label: 'V_MP' },
          { name: 'ChemSpace-BB', label: 'V_CS_BB' },
        ],
      });
    }

    const options: AxiosRequestConfig = {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : undefined,
    };

    return axios.get(`${AppSettings.FRAGNET_SEARCH_SERVER}/${AppSettings.SUPPLIERS}`, options);
  };

  getSuppliers = (mock = true): Promise<any> => {
    if (!this._suppliers) {
      this._suppliers = this._fetchSuppliers().then((res) => res.data);
    }
    return this._suppliers;
  };

  searchNeighbourhood = (smiles: string, searchParams: any, mock = false): Promise<any> => {
    if (mock) {
      return axios.get(
        'https://raw.githubusercontent.com/InformaticsMatters/fragnet/master/fragnet-search/neighbourhood-search.json',
      );
    }
    const fmtCalculations = searchParams.calculations.join(',');
    const options: AxiosRequestConfig = {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : undefined,
      params: {
        ...searchParams,
        groupLimit: searchParams.groupLimit !== null ? searchParams.groupLimit : undefined,
        calcs: fmtCalculations,
        calculations: undefined,
      },
    };

    return axios.get(
      `${AppSettings.FRAGNET_SEARCH_SERVER}/${AppSettings.QUERY}/${encodeURIComponent(smiles)}`,
      options,
    );
  };

  getMolFileFromSmiles(smiles: string, mock = true): Promise<any> {
    return axios.get(
      `${AppSettings.FRAGNET_SEARCH_SERVER}/${AppSettings.MOLFILES}?smiles=${encodeURIComponent(
        smiles,
      )}`,
    );
  }
}

export const isAxiosError = (o: any): o is AxiosError => {
  return o.isAxiosError;
};

const FragnetService = new FragnetServiceImpl();
export default FragnetService;
